import React from 'react';
import { Helmet } from 'react-helmet-async';

const BASE_URL = 'https://everestcloud.com.np';

// SEO configuration for each page
export const SEO_CONFIG = {
    home: {
        title: "EverestCloud | Nepal's Sovereign Enterprise Cloud | Nutanix-Verified",
        description: "Nepal's first Nutanix-verified sovereign cloud. Built on Nutanix — Verified Solution in all four categories. Multi-AZ infrastructure across Kathmandu, Lalitpur & Butwal. NRB compliant for banking, government & enterprise. 99.99% uptime SLA.",
        keywords: "sovereign cloud Nepal, enterprise cloud Nepal, Nutanix verified Nepal, cloud hosting Nepal, data center Nepal, NRB compliance cloud",
        path: "/"
    },
    platform: {
        title: "Cloud Platform | Multi-AZ Infrastructure | EverestCloud Nepal",
        description: "Explore EverestCloud's enterprise cloud platform featuring 3 availability zones across Nepal. Built on Nutanix — Verified Solution in all four categories. 99.99% uptime for mission-critical workloads.",
        keywords: "cloud platform Nepal, multi-AZ infrastructure, Nutanix verified cloud Nepal, enterprise infrastructure Nepal",
        path: "/platform"
    },
    products: {
        title: "Cloud Products & Services | IaaS, DRaaS, Colocation | EverestCloud",
        description: "Comprehensive cloud products: IaaS, DRaaS, Private Cloud, Colocation, and Managed Services. Built for Nepal's banking, government, and enterprise sectors.",
        keywords: "IaaS Nepal, DRaaS Nepal, cloud services Nepal, colocation Nepal, private cloud Nepal, managed services Nepal",
        path: "/products"
    },
    solutions: {
        title: "Industry Solutions | Banking, Government, Enterprise Cloud | EverestCloud",
        description: "Tailored cloud solutions for Nepal's key industries. Banking & Finance, Government, Telecom, Healthcare, and Enterprise solutions with compliance built-in.",
        keywords: "cloud for banks Nepal, government cloud Nepal, enterprise cloud solutions, fintech cloud Nepal, healthcare cloud Nepal",
        path: "/solutions"
    },
    security: {
        title: "Cloud Security | Enterprise-Grade Protection | EverestCloud Nepal",
        description: "Enterprise security for your cloud workloads. Multi-layer protection, DDoS mitigation, encryption at rest and in transit, 24/7 SOC monitoring.",
        keywords: "cloud security Nepal, enterprise security, DDoS protection Nepal, SOC Nepal, cybersecurity Nepal",
        path: "/security"
    },
    compliance: {
        title: "Compliance & Certifications | NRB, ISO 27001 | EverestCloud Nepal",
        description: "Fully compliant cloud infrastructure. NRB guidelines for banking, ISO 27001 certified, PCI DSS ready. Meet Nepal's data residency requirements.",
        keywords: "NRB compliance cloud, ISO 27001 Nepal, PCI DSS Nepal, data residency Nepal, Nepal data localization",
        path: "/compliance"
    },
    planner: {
        title: "Cloud Planner | Design Your Infrastructure | EverestCloud Nepal",
        description: "Design your cloud architecture with our interactive planner. Configure compute, storage, DR, and get personalized recommendations for your workloads.",
        keywords: "cloud planner Nepal, cloud architecture design, infrastructure sizing, cloud calculator Nepal",
        path: "/planner"
    },
    contact: {
        title: "Contact Us | Get Started with EverestCloud | Nepal's Sovereign Cloud",
        description: "Contact EverestCloud for enterprise cloud solutions in Nepal. Request a demo, get pricing, or speak with our cloud experts. Located in Kathmandu.",
        keywords: "contact EverestCloud, cloud provider Nepal contact, enterprise cloud Nepal",
        path: "/contact"
    },
    present: {
        title: "Executive Presentation | EverestCloud Overview",
        description: "Interactive presentation showcasing EverestCloud's sovereign enterprise cloud capabilities, infrastructure, and services for Nepal's enterprises.",
        keywords: "EverestCloud presentation, cloud overview Nepal",
        path: "/present"
    }
};

const SEO = ({ 
    page = 'home',
    title,
    description,
    keywords,
    image,
    article = false,
    noindex = false
}) => {
    const config = SEO_CONFIG[page] || SEO_CONFIG.home;
    
    const seo = {
        title: title || config.title,
        description: description || config.description,
        keywords: keywords || config.keywords,
        image: image || `${BASE_URL}/og-image.png`,
        url: `${BASE_URL}${config.path}`
    };

    return (
        <Helmet>
            {/* Primary Meta Tags */}
            <title>{seo.title}</title>
            <meta name="title" content={seo.title} />
            <meta name="description" content={seo.description} />
            <meta name="keywords" content={seo.keywords} />
            
            {/* Robots */}
            {noindex ? (
                <meta name="robots" content="noindex, nofollow" />
            ) : (
                <meta name="robots" content="index, follow" />
            )}
            
            {/* Canonical */}
            <link rel="canonical" href={seo.url} />
            
            {/* Open Graph / Facebook */}
            <meta property="og:type" content={article ? 'article' : 'website'} />
            <meta property="og:url" content={seo.url} />
            <meta property="og:title" content={seo.title} />
            <meta property="og:description" content={seo.description} />
            <meta property="og:image" content={seo.image} />
            <meta property="og:site_name" content="EverestCloud" />
            
            {/* Twitter */}
            <meta name="twitter:card" content="summary_large_image" />
            <meta name="twitter:url" content={seo.url} />
            <meta name="twitter:title" content={seo.title} />
            <meta name="twitter:description" content={seo.description} />
            <meta name="twitter:image" content={seo.image} />
        </Helmet>
    );
};

export default SEO;
